/**
 * Services / Hooks — الطبقة الوحيدة التي تنادي Provider.
 *
 * أي Component يريد بيانات يستدعي Hook من هنا. لا Component ينادي Provider
 * مباشرة، ولا Component يحسب أي مؤشر.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMarketData } from '@/providers';
import { useAsync, type AsyncState } from './useAsync';
import type {
  CityName,
  DistrictDetail,
  DistrictSuggestion,
  MapMetrics,
  MapMode,
  MarketPulse,
  Opportunity,
  OpportunityFilter,
  PropertyCategoryAvailability,
  ReportListItem,
  ReportPreview,
  ReportType,
  ValuationRequest,
  ValuationResult,
  WatchAlertConditions,
  WatchItem,
} from '@/domain/types';
import { ProviderError, type ProviderErrorCode } from '@/domain/market-data-provider';

export function useCities(): AsyncState<CityName[]> {
  const provider = useMarketData();
  return useAsync(() => provider.getCities(), [provider]);
}

/**
 * أي فئات العقار قابلة للتقييم. `null` أثناء التحميل أو عند الخطأ —
 * والمستهلك يعامل `null` معاملة «غير متاح»: لا نعرض خياراً لم يثبت.
 */
export function usePropertyCategories(): PropertyCategoryAvailability | null {
  const provider = useMarketData();
  const [availability, setAvailability] = useState<PropertyCategoryAvailability | null>(null);

  useEffect(() => {
    let cancelled = false;
    provider
      .getPropertyCategories()
      .then((a) => {
        if (!cancelled) setAvailability(a);
      })
      .catch(() => {
        if (!cancelled) setAvailability(null);
      });
    return () => {
      cancelled = true;
    };
  }, [provider]);

  return availability;
}

export function useDistrictSearch(query: string, limit = 6): DistrictSuggestion[] {
  const provider = useMarketData();
  const [results, setResults] = useState<DistrictSuggestion[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!query.trim()) {
      setResults([]);
      return;
    }
    provider
      .searchDistricts(query, limit)
      .then((r) => {
        if (!cancelled) setResults(r);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [provider, query, limit]);

  return results;
}

export function useDistrict(districtId: string | undefined): AsyncState<DistrictDetail> {
  const provider = useMarketData();
  return useAsync(
    () => provider.getDistrict({ districtId }),
    [provider, districtId],
    { enabled: Boolean(districtId) },
  );
}

export function useMapMetrics(city: CityName, mode: MapMode): AsyncState<MapMetrics> {
  const provider = useMarketData();
  return useAsync(() => provider.getMapMetrics({ city, mode }), [provider, city, mode]);
}

export function useOpportunities(filter: OpportunityFilter): AsyncState<Opportunity[]> {
  const provider = useMarketData();
  const key = JSON.stringify(filter);
  return useAsync(
    () => provider.getOpportunities({ ...filter }),
    [provider, key],
    { isEmpty: (list) => list.length === 0 },
  );
}

export function useMarketPulse(city?: CityName): AsyncState<MarketPulse> {
  const provider = useMarketData();
  return useAsync(() => provider.getMarketPulse(city), [provider, city]);
}

export function useReports(): AsyncState<ReportListItem[]> {
  const provider = useMarketData();
  return useAsync(() => provider.listReports(), [provider], {
    isEmpty: (list) => list.length === 0,
  });
}

export function useReportPreview(type: ReportType, subjectId?: string): AsyncState<ReportPreview> {
  const provider = useMarketData();
  return useAsync(() => provider.getReport(type, subjectId), [provider, type, subjectId]);
}

// ---------------------------------------------------------------------------
// المراقبة — حالة قابلة للتعديل
// ---------------------------------------------------------------------------

export interface WatchlistApi {
  state: AsyncState<WatchItem[]>;
  add: (districtId: string) => Promise<void>;
  remove: (districtId: string) => Promise<void>;
  updateConditions: (districtId: string, conditions: WatchAlertConditions) => Promise<void>;
  isWatched: (districtId: string) => boolean;
}

export function useWatchlist(): WatchlistApi {
  const provider = useMarketData();
  const state = useAsync(() => provider.getWatchlist(), [provider], {
    isEmpty: (list) => list.length === 0,
  });
  const [local, setLocal] = useState<WatchItem[] | null>(null);

  useEffect(() => {
    if (state.data) setLocal(state.data);
  }, [state.data]);

  const items = local ?? state.data ?? [];

  const add = useCallback(
    async (districtId: string) => {
      setLocal(await provider.addToWatchlist({ districtId }));
    },
    [provider],
  );
  const remove = useCallback(
    async (districtId: string) => {
      setLocal(await provider.removeFromWatchlist(districtId));
    },
    [provider],
  );
  const updateConditions = useCallback(
    async (districtId: string, conditions: WatchAlertConditions) => {
      setLocal(await provider.updateWatchConditions(districtId, conditions));
    },
    [provider],
  );
  const isWatched = useCallback(
    (districtId: string) => items.some((w) => w.districtId === districtId),
    [items],
  );

  const merged = useMemo<AsyncState<WatchItem[]>>(
    () => ({
      status: local ? (local.length === 0 ? 'empty' : 'success') : state.status,
      data: items,
      error: state.error,
      reload: state.reload,
    }),
    [local, items, state.status, state.error, state.reload],
  );

  return { state: merged, add, remove, updateConditions, isWatched };
}

// ---------------------------------------------------------------------------
// التقييم — إجراء يبدأ بطلب المستخدم لا بتحميل الصفحة
// ---------------------------------------------------------------------------

export type ValuationStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'no-data'
  | 'unavailable'
  | 'error';

export interface ValuationApi {
  status: ValuationStatus;
  result: ValuationResult | null;
  error: {
    code: ProviderErrorCode | 'unknown';
    message: string;
    /** تشخيص الرفض بالأرقام الفعلية — يُعرض بدل نص ثابت. */
    rejection?: { headline: string; facts: string[]; suggestion: string | null } | null;
  } | null;
  /** آخر مدخلات — محفوظة حتى في حالة الخطأ (شرط في التصميم المعتمد). */
  lastRequest: ValuationRequest | null;
  run: (request: ValuationRequest) => Promise<void>;
  retry: () => Promise<void>;
  reset: () => void;
}

export function useValuation(): ValuationApi {
  const provider = useMarketData();
  const [status, setStatus] = useState<ValuationStatus>('idle');
  const [result, setResult] = useState<ValuationResult | null>(null);
  const [error, setError] = useState<ValuationApi['error']>(null);
  const [lastRequest, setLastRequest] = useState<ValuationRequest | null>(null);

  const run = useCallback(
    async (request: ValuationRequest) => {
      setLastRequest(request);
      setStatus('loading');
      setError(null);
      try {
        const r = await provider.getValuation(request);
        setResult(r);
        setStatus('success');
      } catch (err) {
        setResult(null);
        if (err instanceof ProviderError) {
          setError({ code: err.code, message: err.message, rejection: err.rejection ?? null });
          setStatus(
            err.code === 'insufficient_data'
              ? 'no-data'
              : err.code === 'feature_unavailable'
                ? 'unavailable'
                : 'error',
          );
        } else {
          setError({ code: 'unknown', message: 'حدث خطأ غير متوقع.' });
          setStatus('error');
        }
      }
    },
    [provider],
  );

  const retry = useCallback(async () => {
    if (lastRequest) await run(lastRequest);
  }, [lastRequest, run]);

  const reset = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setError(null);
  }, []);

  return { status, result, error, lastRequest, run, retry, reset };
}
